import React, { useRef, useState, useEffect, useCallback } from 'react';
import * as d3 from 'd3';
import { Box, useTheme } from '@mui/material';
import { CounterRow } from './CutterDetailsHistory.counters';
import { getZipFileLink } from '../../utils/dateUtils';

export interface CountersLineChartProps {
    rows: CounterRow[];
    selectedCounters: string[];
    xDomainOverride?: [Date, Date] | null;
    onBrushChange?: (range: [Date, Date] | null) => void;
}

// ── Colour palette ─────────────────────────────────────────────────────────
// 20-colour categorical scale, cycling for large counter sets.
const COLOR_PALETTE = [
    '#1565c0', '#b71c1c', '#2e7d32', '#e65100', '#6a1b9a',
    '#00838f', '#f9a825', '#3e2723', '#37474f', '#4a148c',
    '#0277bd', '#558b2f', '#c62828', '#4527a0', '#00695c',
    '#283593', '#6d4c41', '#0d47a1', '#1b5e20', '#bf360c',
];

const lineColor = (index: number): string => COLOR_PALETTE[index % COLOR_PALETTE.length];

// ── D3 tick format helpers ─────────────────────────────────────────────────
const tickFormatForRange = (spanMs: number): string => {
    if (spanMs < 3_600_000)   return '%H:%M';
    if (spanMs < 86_400_000)  return '%b %d %H:%M';
    if (spanMs < 604_800_000) return '%b %d';
    return "%b '%y";
};

// ── Component ──────────────────────────────────────────────────────────────
export const CountersLineChart: React.FC<CountersLineChartProps> = ({ rows, selectedCounters, xDomainOverride, onBrushChange }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const onBrushChangeRef = useRef(onBrushChange);
    onBrushChangeRef.current = onBrushChange;
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const theme = useTheme();
    const [hoveredZipInfo, setHoveredZipInfo] = useState<{ fileName: string; link?: string } | null>(null);

    const updateDimensions = useCallback(() => {
        if (containerRef.current) {
            const w = containerRef.current.clientWidth;
            const h = containerRef.current.clientHeight;
            if (w > 0) setContainerWidth(w);
            if (h > 0) setContainerHeight(h);
        }
    }, []);

    useEffect(() => {
        updateDimensions();
        const ro = new ResizeObserver(updateDimensions);
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [updateDimensions]);

    useEffect(() => {
        if (!svgRef.current || containerWidth === 0 || rows.length === 0 || selectedCounters.length === 0) return;

        d3.select(svgRef.current).selectAll('*').remove();

        const legendRows = Math.ceil(selectedCounters.length / 3);
        const legendHeight = legendRows * 22;
        const margin = { top: 16, right: 24, bottom: 50 + legendHeight, left: 70 };
        const totalHeight = containerHeight > 200 ? containerHeight : 380;
        const width  = containerWidth  - margin.left - margin.right;
        const height = totalHeight - margin.top - margin.bottom;

        // ── Scales ──────────────────────────────────────────────────────────
        const naturalDomain: [Date, Date] = [
            rows[0].zipCreationDate,
            rows[rows.length - 1].zipCreationDate,
        ];
        const xDomain = xDomainOverride ?? naturalDomain;
        const xScale = d3.scaleTime().domain(xDomain).range([0, width]);

        // Per-counter y-scales (shared or individual — individual for readability with different magnitudes)
        const yScales: Record<string, d3.ScaleLinear<number, number>> = {};
        for (const key of selectedCounters) {
            const vals = rows.map(r => r[key] as number).filter(v => v != null && !isNaN(v));
            if (vals.length === 0) {
                yScales[key] = d3.scaleLinear().domain([0, 1]).range([height, 0]);
            } else {
                const [mn, mx] = d3.extent(vals) as [number, number];
                const pad = mx === mn ? Math.abs(mn) * 0.1 || 1 : (mx - mn) * 0.05;
                yScales[key] = d3.scaleLinear().domain([mn - pad, mx + pad]).nice().range([height, 0]);
            }
        }

        // ── SVG root ─────────────────────────────────────────────────────────
        const svg = d3.select(svgRef.current)
            .attr('width',  containerWidth)
            .attr('height', totalHeight)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // ── Clip path ────────────────────────────────────────────────────────
        svg.append('defs').append('clipPath').attr('id', 'counters-clip')
            .append('rect').attr('width', width).attr('height', height);

        // ── X axis ───────────────────────────────────────────────────────────
        const spanMs = xDomain[1].getTime() - xDomain[0].getTime();
        const maxTicks = Math.max(2, Math.floor(width / 80));
        svg.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(xScale)
                    .ticks(Math.min(maxTicks, rows.length))
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .tickFormat(d3.timeFormat(tickFormatForRange(spanMs)) as any)
            )
            .selectAll('text')
            .attr('transform', 'rotate(-35)')
            .style('text-anchor', 'end')
            .style('font-size', '11px')
            .style('fill', theme.palette.text.primary);
        svg.selectAll('.domain, .tick line').style('stroke', theme.palette.text.secondary);

        // ── Y axis (left — uses first selected counter scale) ────────────────
        if (selectedCounters.length > 0) {
            const firstKey = selectedCounters[0];
            svg.append('g')
                .call(d3.axisLeft(yScales[firstKey]).ticks(6).tickFormat(d3.format('.3~s')))
                .selectAll('text')
                .style('fill', lineColor(0))
                .style('font-size', '11px');
            svg.selectAll('.tick line').style('stroke', theme.palette.divider);
        }

        // ── Grid lines ───────────────────────────────────────────────────────
        if (selectedCounters.length > 0) {
            const firstKey = selectedCounters[0];
            svg.append('g')
                .attr('class', 'grid')
                .call(
                    d3.axisLeft(yScales[firstKey])
                        .ticks(6)
                        .tickSize(-width)
                        .tickFormat(() => '')
                )
                .selectAll('.tick line')
                .style('stroke', theme.palette.divider)
                .style('stroke-dasharray', '3,3')
                .style('opacity', '0.5');
            svg.select('.grid .domain').remove();
        }

        // ── Tooltip tracking line ────────────────────────────────────────────
        const hoverLine = svg.append('line')
            .attr('class', 'hover-line')
            .attr('y1', 0).attr('y2', height)
            .attr('stroke', theme.palette.text.secondary)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '4,3')
            .style('opacity', 0)
            .style('pointer-events', 'none');

        // ── Lines + dots per counter ─────────────────────────────────────────
        const chartGroup = svg.append('g').attr('clip-path', 'url(#counters-clip)');

        selectedCounters.forEach((key, idx) => {
            const color = lineColor(idx);
            const yScale = yScales[key];

            // Filter out points with no value for this counter
            const validRows = rows.filter(r => r[key] != null && !isNaN(r[key] as number));
            if (validRows.length === 0) return;

            const lineGenerator = d3.line<CounterRow>()
                .x(r => xScale(r.zipCreationDate))
                .y(r => yScale(r[key] as number))
                .defined(r => r[key] != null && !isNaN(r[key] as number))
                .curve(d3.curveMonotoneX);

            chartGroup.append('path')
                .datum(validRows)
                .attr('fill', 'none')
                .attr('stroke', color)
                .attr('stroke-width', 1.8)
                .attr('opacity', 0.85)
                .attr('d', lineGenerator);

            // Dots (only when ≤ 60 points — otherwise too cluttered)
            if (validRows.length <= 60) {
                chartGroup.selectAll<SVGCircleElement, CounterRow>(`.dot-${idx}`)
                    .data(validRows)
                    .enter()
                    .append('circle')
                    .attr('class', `dot-${idx}`)
                    .attr('cx', r => xScale(r.zipCreationDate))
                    .attr('cy', r => yScale(r[key] as number))
                    .attr('r', 3)
                    .attr('fill', color)
                    .attr('stroke', theme.palette.background.paper)
                    .attr('stroke-width', 1)
                    .style('cursor', 'crosshair');
            }
        });

        // ── Brush for range selection ────────────────────────────────────────
        // The brush owns the top-level overlay rect (pointer-events), so tooltip
        // events are wired directly onto the brush's .overlay and .selection.
        const brushGroup = svg.append('g').attr('class', 'x-brush');

        const brush = d3.brushX()
            .extent([[0, 0], [width, height]])
            .on('end', (event: d3.D3BrushEvent<unknown>) => {
                if (!event.selection) {
                    onBrushChangeRef.current?.(null);
                    return;
                }
                const [x0, x1] = event.selection as [number, number];
                onBrushChangeRef.current?.([xScale.invert(x0), xScale.invert(x1)]);
            });

        brushGroup.call(brush);

        // Style the selection rectangle
        brushGroup.select<SVGRectElement>('.selection')
            .style('fill', theme.palette.primary.main)
            .style('fill-opacity', '0.12')
            .style('stroke', theme.palette.primary.main)
            .style('stroke-width', '1');

        // ── Tooltip helper (shared by overlay and selection) ─────────────────
        const showTooltip = (event: MouseEvent, target: SVGRectElement): void => {
            const [mouseX] = d3.pointer(event, target);
            const hoveredDate = xScale.invert(mouseX);

            let closestIdx = 0;
            let minDiff = Infinity;
            rows.forEach((r, i) => {
                const diff = Math.abs(r.zipCreationDate.getTime() - hoveredDate.getTime());
                if (diff < minDiff) { minDiff = diff; closestIdx = i; }
            });
            const closestRow = rows[closestIdx];
            const xPos = xScale(closestRow.zipCreationDate);
            hoverLine.attr('x1', xPos).attr('x2', xPos).style('opacity', 1);

            const dateStr = closestRow.zipCreationDate.toLocaleString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
            const rows_html = selectedCounters
                .filter(k => closestRow[k] != null)
                .map((k, idx) => {
                    const v = closestRow[k] as number;
                    const formatted = Number.isInteger(v) ? v.toString() : v.toFixed(4);
                    return `<tr><td style="color:${lineColor(idx)};padding-right:8px;white-space:nowrap">${k}</td><td style="text-align:right;white-space:nowrap"><strong>${formatted}</strong></td></tr>`;
                })
                .join('');

            setHoveredZipInfo({
                fileName: closestRow.zipFileName,
                link: getZipFileLink(closestRow.zipFileName, closestRow.zipStoredOnDisk),
            });

            d3.selectAll('.counters-tooltip').remove();
            const flipRight = event.clientX > containerWidth - 200;
            d3.select('body').append('div')
                .attr('class', 'counters-tooltip')
                .style('position', 'fixed')
                .style('background', 'rgba(20,20,20,0.92)')
                .style('color', '#fff')
                .style('padding', '8px 10px')
                .style('border-radius', '4px')
                .style('pointer-events', 'none')
                .style('z-index', '2000')
                .style('font-size', '11px')
                .style('max-width', '320px')
                .style('left', `${event.clientX + (flipRight ? -180 : 14)}px`)
                .style('top', `${event.clientY - 10}px`)
                .html(`<div style="font-size:10px;opacity:0.75;margin-bottom:4px">${dateStr}</div><table>${rows_html}</table>`);
        };

        const hideTooltip = (): void => {
            hoverLine.style('opacity', 0);
            d3.selectAll('.counters-tooltip').remove();
        };

        brushGroup.select<SVGRectElement>('.overlay')
            .style('cursor', 'crosshair')
            .on('mousemove', function(event: MouseEvent) { showTooltip(event, this); })
            .on('mouseout', hideTooltip);

        brushGroup.select<SVGRectElement>('.selection')
            .on('mousemove', function(event: MouseEvent) { showTooltip(event, this); })
            .on('mouseout', hideTooltip);

        // ── Mouse wheel zoom ──────────────────────────────────────────────────
        // Registered as non-passive so preventDefault() suppresses page scroll.
        const overlayNode = brushGroup.select<SVGRectElement>('.overlay').node();
        if (overlayNode) {
            overlayNode.addEventListener('wheel', (event: WheelEvent) => {
                event.preventDefault();
                const [mouseX] = d3.pointer(event, overlayNode);
                const mouseMs  = xScale.invert(mouseX).getTime();
                const nat0 = naturalDomain[0].getTime();
                const nat1 = naturalDomain[1].getTime();
                const cur0 = xDomain[0].getTime();
                const cur1 = xDomain[1].getTime();
                // scroll up (deltaY < 0) → zoom in (factor < 1); scroll down → zoom out
                const factor = event.deltaY < 0 ? 0.75 : 1 / 0.75;
                const newLeft  = Math.max(mouseMs - (mouseMs - cur0) * factor, nat0);
                const newRight = Math.min(mouseMs + (cur1 - mouseMs) * factor, nat1);
                if (newRight - newLeft < 86_400_000) return;   // never zoom below 1 day
                if (newLeft <= nat0 && newRight >= nat1) {
                    onBrushChangeRef.current?.(null);           // fully zoomed out → clear
                } else {
                    onBrushChangeRef.current?.([new Date(newLeft), new Date(newRight)]);
                }
            }, { passive: false });
        }

        // ── Legend (bottom, 3 per row) ───────────────────────────────────────
        const legendGroup = svg.append('g').attr('transform', `translate(0,${height + 46})`);
        const legendColWidth = Math.floor(width / 3);

        selectedCounters.forEach((key, idx) => {
            const col = idx % 3;
            const row = Math.floor(idx / 3);
            const g = legendGroup.append('g').attr('transform', `translate(${col * legendColWidth},${row * 22})`);
            g.append('rect').attr('width', 12).attr('height', 3).attr('y', 5).attr('fill', lineColor(idx)).attr('rx', 1);
            g.append('text')
                .attr('x', 18).attr('y', 12)
                .style('font-size', '11px')
                .style('fill', theme.palette.text.primary)
                .text(key);
        });

    }, [rows, selectedCounters, containerWidth, containerHeight, theme, xDomainOverride]);

    // Clean up any orphaned tooltip on unmount
    useEffect(() => () => { d3.selectAll('.counters-tooltip').remove(); }, []);

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Zip file info bar — always rendered (fixed height) to avoid layout shift */}
            <Box sx={{ height: 22, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, px: 0.5,
                visibility: hoveredZipInfo ? 'visible' : 'hidden' }}>
                <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    {hoveredZipInfo?.fileName}.zip
                </Box>
                {hoveredZipInfo?.link && (
                    <Box component="a" href={hoveredZipInfo.link}
                        download={`${hoveredZipInfo.fileName}.zip`}
                        sx={{ fontSize: '0.7rem', color: 'primary.main', textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' } }}>
                        ⬇ Download
                    </Box>
                )}
            </Box>
            <Box ref={containerRef} sx={{ flex: 1, minHeight: 'calc(60vh - 48px)', overflow: 'auto' }}>
                <svg ref={svgRef} style={{ width: '100%', display: 'block' }} />
            </Box>
        </Box>
    );
};

export default CountersLineChart;
